import React, { useState } from "react";
import api from "../services/api";
import FeedbackSection from "./FeedbackSection";

/**
 * Floating launcher shown at the bottom-right after the user hides the Access
 * popup, so they can always re-open the feedback / extension form.
 */
export function TrialFloatingButton({ scenario, onClick }) {
  const [hovered, setHovered] = useState(false);
  const isExpired = scenario === "expired";
  const color = isExpired ? "#f87171" : "#f59e0b";
  const bg = isExpired
    ? "linear-gradient(135deg, #ef4444, #b91c1c)"
    : "linear-gradient(135deg, #f59e0b, #d97706)";

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={
        isExpired
          ? "Your access period has expired — request an extension"
          : "Your access period is expiring soon"
      }
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 8000,
        display: "inline-flex",
        alignItems: "center",
        gap: "9px",
        padding: hovered ? "12px 20px" : "12px 16px",
        background: bg,
        color: "#fff",
        border: "none",
        borderRadius: "30px",
        fontSize: "13px",
        fontWeight: "700",
        cursor: "pointer",
        boxShadow: `0 8px 24px ${color}55, 0 2px 8px rgba(0,0,0,0.3)`,
        transition: "padding 0.18s ease",
      }}
    >
      <span style={{ fontSize: "16px", lineHeight: 1 }}>
        {isExpired ? "⏰" : "⚠️"}
      </span>
      <span
        style={{
          maxWidth: hovered ? "240px" : "0",
          overflow: "hidden",
          whiteSpace: "nowrap",
          transition: "max-width 0.18s ease",
        }}
      >
        {isExpired
          ? "Access period expired — request extension"
          : "Access period expiring soon"}
      </span>
    </button>
  );
}

function Spinner() {
  return (
    <span
      style={{
        display: "inline-block",
        width: "13px",
        height: "13px",
        border: "2px solid currentColor",
        borderTopColor: "transparent",
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
      }}
    />
  );
}

export default function TrialNotificationModal({
  open = true,
  scenario,
  onDismiss,
  submitted = { feedback: false, extension: false, rating: null },
  onSubmitted,
  onRatingSaved,
}) {
  // Extension-request section (a single button — no form)
  const [extSubmitting, setExtSubmitting] = useState(false);
  const [extSubmittedLocal, setExtSubmittedLocal] = useState(false);
  const [extError, setExtError] = useState("");

  const isExpired = scenario === "expired";
  const extSubmitted = extSubmittedLocal || !!submitted.extension;

  const submitExtension = async () => {
    setExtError("");
    setExtSubmitting(true);
    try {
      await api.submitTrialFeedback({ type: "extension" });
      setExtSubmittedLocal(true);
      onSubmitted?.("extension");
    } catch (err) {
      setExtError("Something went wrong. Please try again.");
    } finally {
      setExtSubmitting(false);
    }
  };

  // Hidden (but still mounted) when closed — preserves submitted state so
  // re-opening via the floating button keeps everything the user already did.
  if (!open) return null;

  const accentColor = isExpired ? "#f87171" : "#f59e0b";
  const accentBg = isExpired ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)";
  const accentBorder = isExpired
    ? "rgba(239,68,68,0.25)"
    : "rgba(245,158,11,0.25)";

  const errorLine = (msg) =>
    msg ? (
      <span
        style={{
          fontSize: "12px",
          color: "#f87171",
          display: "block",
          marginTop: "8px",
        }}
      >
        ⚠ {msg}
      </span>
    ) : null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        style={{
          background: "var(--bg-card, #0d1829)",
          border: `1px solid ${accentBorder}`,
          borderRadius: "16px",
          width: "100%",
          maxWidth: "520px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: `0 0 60px ${accentBg}, 0 24px 64px rgba(0,0,0,0.6)`,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${accentBorder}`,
            background: accentBg,
            borderRadius: "16px 16px 0 0",
          }}
        >
          <div
            style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}
          >
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: isExpired
                  ? "rgba(239,68,68,0.15)"
                  : "rgba(245,158,11,0.15)",
                border: `1px solid ${accentBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "16px",
              }}
            >
              {isExpired ? "⏰" : "⚠️"}
            </div>
            <div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: accentColor,
                  marginBottom: "4px",
                }}
              >
                {isExpired
                  ? "Your Access Period has expired."
                  : "Your Access Period will expire soon."}
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  margin: 0,
                  lineHeight: "1.55",
                }}
              >
                {isExpired ? (
                  <>
                    Your Access period has expired. We would love to hear your
                    feedback — please leave a couple of suggestions that can
                    help us improve this product below. You can request for an
                    extension from the request button below or reach out to our
                    sales team at{" "}
                    <a
                      href="mailto:sales@saffronedge.com"
                      style={{
                        color: "var(--primary-color)",

                        textDecoration: "underline",
                      }}
                    >
                      sales@saffronedge.com
                    </a>{" "}
                    if you'd like to extend your access
                  </>
                ) : (
                  <>
                    Your access will end within the next 24 hours.You can
                    request for an extension from the request button below or
                    reach out to our sales team at{" "}
                    <a
                      href="mailto:sales@saffronedge.com"
                      style={{
                        color: "var(--primary-color)",

                        textDecoration: "underline",
                      }}
                    >
                      sales@saffronedge.com
                    </a>{" "}
                    if you'd like to extend your access.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Extension request + feedback — shown for both the expiring and
            expired scenarios. */}
        <div style={{ padding: "20px 24px" }}>
          {/* Extension request — label and button on the same line, no form */}
          {extSubmitted ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "12px 14px",
                borderRadius: "10px",
                background: "rgba(16,185,129,0.08)",
                border: "1px solid rgba(16,185,129,0.25)",
              }}
            >
              <span style={{ fontSize: "16px" }}>✓</span>
              <span
                style={{
                  fontSize: "13px",
                  color: "#34d399",
                  fontWeight: 600,
                }}
              >
                Request received — our sales team will contact you within 48
                hours.
              </span>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "var(--text-primary)",
                  }}
                >
                  Request for an Extension
                </span>
                <button
                  type="button"
                  onClick={submitExtension}
                  disabled={extSubmitting}
                  className="btn btn-secondary"
                  style={{
                    padding: "8px 18px",
                    fontSize: "13px",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "7px",
                    flexShrink: 0,
                  }}
                >
                  {extSubmitting ? (
                    <>
                      <Spinner /> Sending…
                    </>
                  ) : (
                    "Request"
                  )}
                </button>
              </div>
              {errorLine(extError)}
            </>
          )}

          {/* Feedback — shared component (rating auto-saves; note sends separately) */}
          <div style={{ marginTop: "22px" }}>
            <FeedbackSection
              savedRating={submitted.rating}
              noteAlreadySubmitted={!!submitted.feedback}
              onNoteSubmitted={() => onSubmitted?.("feedback")}
              onRatingSaved={onRatingSaved}
            />
          </div>
        </div>

        {/* Footer — close/dismiss only */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onDismiss}
            className="btn btn-secondary"
            style={{
              fontSize: "13px",
              padding: "9px 18px",
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
